import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'ru', label: 'RU', flag: '🇷🇺' },
  { code: 'bg', label: 'BG', flag: '🇧🇬' },
  { code: 'it', label: 'IT', flag: '🇮🇹' },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0d0d0d]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-shield-star-fill text-[#d4af37] text-2xl"></i>
          </div>
          <span className="text-white font-bold text-xl tracking-widest uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Rheinmetall
          </span>
        </div>

        <div className="hidden md:flex items-center gap-8">
          {[
            { label: t('nav.about'), id: 'about' },
            { label: t('nav.weapons'), id: 'weapons' },
            { label: t('nav.conflicts'), id: 'conflicts' },
            { label: t('nav.calculator'), id: 'calculator' },
            { label: t('nav.contact'), id: 'contacts' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-white/80 hover:text-[#d4af37] text-sm tracking-wider uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          {/* Language Switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 text-white/80 hover:text-[#d4af37] text-sm font-medium px-3 py-1.5 rounded-full border border-white/20 hover:border-[#d4af37]/50 transition-all duration-200 cursor-pointer"
            >
              <span>{currentLang.flag}</span>
              <span>{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-xl min-w-[120px]">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      i18n.language === lang.code
                        ? 'bg-[#d4af37]/20 text-[#d4af37]'
                        : 'text-white/80 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => scrollTo('calculator')}
            className="bg-[#d4af37] text-black text-sm font-bold px-6 py-2.5 rounded-full hover:bg-[#e8c84a] transition-all duration-200 cursor-pointer whitespace-nowrap tracking-wider uppercase"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t('hero.cta1')}
          </button>
        </div>

        <button
          className="md:hidden text-white text-2xl cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-[#0d0d0d]/98 px-6 py-4 flex flex-col gap-4">
          {[
            { label: t('nav.about'), id: 'about' },
            { label: t('nav.weapons'), id: 'weapons' },
            { label: t('nav.conflicts'), id: 'conflicts' },
            { label: t('nav.calculator'), id: 'calculator' },
            { label: t('nav.contact'), id: 'contacts' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-white/80 hover:text-[#d4af37] text-sm tracking-wider uppercase transition-colors duration-200 cursor-pointer text-left whitespace-nowrap"
            >
              {item.label}
            </button>
          ))}
          {/* Mobile Language Switcher */}
          <div className="flex gap-2 pt-2 border-t border-white/10">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors duration-200 cursor-pointer ${
                  i18n.language === lang.code
                    ? 'bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/30'
                    : 'text-white/60 border border-white/10 hover:border-white/30'
                }`}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => scrollTo('calculator')}
            className="bg-[#d4af37] text-black text-sm font-bold px-6 py-2.5 rounded-full hover:bg-[#e8c84a] transition-all duration-200 cursor-pointer whitespace-nowrap tracking-wider uppercase w-fit"
          >
            {t('hero.cta1')}
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;